// The competition counts page (MC2 76145074): every competition of a feis by
// level, as the entry card lists dances, with how many dancers are in each,
// and the family's own competitions on top. Who is in a competition only
// once the organizer marks merges and splits done (the list then carries
// names; Devin 2026-10-18). Plain functions.
import { summarySections } from "../Register/registrationModel";
import { ageLabel, countsByCode } from "../../Components/EntryCard/entryCardModel";

const nameOf = (row) => [row?.dancerData?.fname, row?.dancerData?.lname].filter(Boolean).join(" ");

// Who is in each competition, by code, when the list carries names.
const dancersByCode = (rows) => {
  const m = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r?.dancerData?.fname) continue;
    for (const c of r?.competitonData || []) {
      if (!c?.code) continue;
      if (!m.has(c.code)) m.set(c.code, []);
      m.get(c.code).push({ id: r._id, number: r.card_number, name: nameOf(r), school: r.dancerData.school || "" });
    }
  }
  for (const list of m.values()) list.sort((a, b) => (a.number || 0) - (b.number || 0) || a.name.localeCompare(b.name));
  return m;
};

// Sections by level (Specials, Teams... after), each a list of competitions
// with their 2nd chance under them: { id, code, name, age, count, dancers, subs }
// (dancers empty while names are hidden).
export const competitionSections = (syllabus, entryGroups, rows) => {
  const comps = Array.isArray(syllabus) ? syllabus : [];
  const index = new Map(comps.map((c) => [String(c._id), c]));
  const counts = countsByCode(rows);
  const who = dancersByCode(rows);
  const item = (r) => {
    const c = index.get(r.id);
    return { id: r.id, code: c?.code || "", name: r.secondOnly ? `${r.name} · 2nd chance` : r.name, age: ageLabel(c), count: counts.get(c?.code) || 0, dancers: who.get(c?.code) || [] };
  };
  return summarySections([...index.keys()], index, entryGroups).map((sec) => {
    const items = [];
    for (const r of sec.rows) {
      if (r.sub && items.length) items[items.length - 1].subs.push(item(r));
      else items.push({ ...item(r), subs: [] });
    }
    const count = items.reduce((n, i) => n + i.count + i.subs.reduce((m, s) => m + s.count, 0), 0);
    return { key: sec.key, title: sec.title, items, count };
  });
};

const has = (text, q) => String(text || "").toLowerCase().includes(q);

// A search keeps the competitions whose name, age group or code match.
// Competitions nobody has entered yet are left out unless asked for (a 2nd
// chance with dancers keeps its dance).
export const filterSections = (sections, query, { empty = false } = {}) => {
  const q = String(query || "").trim().toLowerCase();
  const hit = (i) => !q || has(`${i.name} ${i.age}`, q) || has(i.code, q);
  const keep = (i) => (empty || i.count > 0) && hit(i);
  return sections
    .map((sec) => ({
      ...sec,
      items: sec.items
        .filter((i) => keep(i) || i.subs.some(keep))
        .map((i) => ({ ...i, subs: i.subs.filter((s) => empty || s.count > 0) })),
    }))
    .filter((sec) => sec.items.length > 0);
};

// The family's competitions (from their registrations for the event), in
// the page's order, each with its level and which of their dancers are in it.
export const yourCompetitions = (sections, registrations) => {
  const who = new Map();
  for (const reg of Array.isArray(registrations) ? registrations : []) {
    const fname = reg?.dancerData?.fname || "";
    for (const c of reg?.competitionDetails || reg?.entries || []) {
      if (!c?.code) continue;
      if (!who.has(c.code)) who.set(c.code, []);
      if (fname && !who.get(c.code).includes(fname)) who.get(c.code).push(fname);
    }
  }
  const out = [];
  for (const sec of sections) {
    for (const i of sec.items) {
      for (const x of [i, ...i.subs]) {
        if (who.has(x.code)) out.push({ ...x, name: x === i ? i.name : `${i.name} · 2nd chance`, level: sec.title, yours: who.get(x.code) });
      }
    }
  }
  return out;
};

// A teacher's school's competitions (from the school's own list), in the
// page's order: each with how many of the school's dancers are in it and
// who they are, and the full count from the whole list when it is open.
export const schoolCompetitions = (schoolSections, allSections) => {
  const total = new Map();
  for (const sec of allSections || []) for (const i of sec.items) for (const x of [i, ...i.subs]) total.set(x.id, x.count);
  const out = [];
  for (const sec of schoolSections || []) {
    for (const i of sec.items) {
      for (const x of [i, ...i.subs]) {
        if (!x.count) continue;
        out.push({
          ...x,
          name: x === i ? i.name : `${i.name} · 2nd chance`,
          age: i.age,
          level: sec.title,
          fromSchool: x.count,
          count: total.has(x.id) ? total.get(x.id) : x.count,
        });
      }
    }
  }
  return out;
};

// A teacher's school's dancers (their own list), each with the competitions
// they are entered in, in the page's order, with the full count where open.
export const schoolDancers = (schoolRows, schoolList) => {
  const byCode = new Map((schoolList || []).map((x) => [x.code, x]));
  const order = new Map((schoolList || []).map((x, i) => [x.code, i]));
  return (Array.isArray(schoolRows) ? schoolRows : [])
    .map((r) => ({
      id: r._id,
      number: r.card_number,
      name: [r?.dancerData?.fname, r?.dancerData?.lname].filter(Boolean).join(" "),
      comps: (r?.competitonData || [])
        .map((c) => byCode.get(c?.code))
        .filter(Boolean)
        .sort((a, b) => order.get(a.code) - order.get(b.code)),
    }))
    .filter((d) => d.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};
